import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { CheckoutProps } from "@/react/components/Paywalls/Checkout/CheckoutProps";
import type { UsePaywallCheckoutReturn } from "./usePaywallCheckout";
import { usePaywallCheckout } from "./usePaywallCheckout";

interface BaseCheckoutContextAndProviderProps extends CheckoutProps {
  // Buy Now Pay Later provided properties
  paymentMethodsSupported: string[];
  disabledPaymentMethods: string[];
  setDisabledPaymentMethods: (value: string[]) => void;

  // Stripe Elements provided properties
  paymentElementKey: string;
}

export interface CheckoutContextProps
  extends BaseCheckoutContextAndProviderProps,
    UsePaywallCheckoutReturn {
  // Buy Now Pay Later state properties
  isBuyNowPayLaterAllowed: boolean; // This is a state that can be dynamically turned on/off
  setIsBuyNowPayLaterAllowed: (value: boolean) => void;
}

const initialState: CheckoutContextProps = {} as CheckoutContextProps;
const CheckoutContext = createContext<CheckoutContextProps>(initialState);

CheckoutContext.displayName = "CheckoutContext";

export interface CheckoutContextProviderProps
  extends BaseCheckoutContextAndProviderProps {
  children: ReactNode;
}

export const PaywallCheckoutContextProvider = (
  props: CheckoutContextProviderProps,
) => {
  const {
    children,
    isMemberAddressRequired,
    paywall,
    isTaxIdMandatory,
    isMemberWithSavedPaymentMethods,
    paymentElementKey,
    disabledPaymentMethods,
    setDisabledPaymentMethods,
    isPaywallsCheckoutCustomizationPhase1Enabled,
  } = props;

  const {
    applyCouponCode,
    clearCouponCode,
    enableTaxIdCollection,
    getSelectedPrice,
    isTaxIdCollectionEnabled,
    paymentError,
    previewMutation,
    setMemberAddressAttributes,
    setPaymentError,
    setSelectedPrice,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoRequired,
    isSetupIntentRequired,
    handleError,
    isProcessingPayment,
    setIsProcessingPayment,
    isMemberUsingSavedPaymentMethod,
    setIsMemberUsingSavedPaymentMethod,
    ...restContext
  } = usePaywallCheckout({
    isMemberAddressRequired,
    isTaxIdMandatory,
    paywall,
    isMemberWithSavedPaymentMethods,
    isPaywallsCheckoutCustomizationPhase1Enabled,
  });

  const value: CheckoutContextProps = {
    ...initialState,
    ...props,
    applyCouponCode,
    clearCouponCode,
    enableTaxIdCollection,
    getSelectedPrice,
    isTaxIdCollectionEnabled,
    paymentError,
    previewMutation,
    setMemberAddressAttributes,
    setPaymentError,
    setSelectedPrice,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoRequired,
    isSetupIntentRequired,
    handleError,
    isProcessingPayment,
    setIsProcessingPayment,
    isMemberUsingSavedPaymentMethod,
    setIsMemberUsingSavedPaymentMethod,
    paymentElementKey,
    disabledPaymentMethods,
    setDisabledPaymentMethods,
    ...restContext,
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const usePaywallCheckoutContext = () => useContext(CheckoutContext);
